import styled from '@emotion/styled';
import { ifProp } from 'styled-tools';
import Logo from '~/icons/logo.svg';
import { Link } from '~/shared/components';
import { Container } from '~/shared/components/Container';
import { ResetButton } from '~/shared/components/Button/ResetButton';
import { theme } from '~/theme';
import { breakpoints } from '~/theme/breakpoints';
import { mq } from '~/lib/helpers/media-query.helper';
import { Banner } from '~/shared/components/Banner';

type StyledIconButtonProps = {
    active?: boolean;
    mobileOnly?: boolean;
    full?: boolean;
    desktop?: boolean;
    textColor?: string;
    reverseIcon?: boolean;
};

export const StyledMainNavigation = styled.header(
    ({ theme }) => ({
        top: 0,
        transition: '1s',
        position: 'sticky',
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        boxShadow: `0 2px 20px 0 rgba(0,0,0,0.2)`,
        backgroundColor: theme.colors.headerColor,
        zIndex: theme.zIndices[3],
        [breakpoints.xs]: {
            opacity: 1,
            position: 'sticky',
            transition: 'all 0.5s ease-in-out',
            padding: `0 ${theme.space[3]} 0 ${theme.space[3]}`,
        },
    }),
    ifProp('hidden', {
        transform: 'translateY(-100%)',
        transition: 'all 0.5s ease-in-out',
        opacity: 0,
    }),
);

export const StyledContent = styled(Container)<{
    topBanner?: boolean;
    desktop?: boolean;
}>(
    ({ theme }) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        alignContent: 'center',
        padding: `${theme.space[3]} 0 ${theme.space[3]} 0`,

        [breakpoints.xs]: {
            padding: 0,
        },
    }),
    ifProp('topBanner', ({ theme }) => ({
        padding: 0,
        maxWidth: 'unset',
        [breakpoints.xs]: {
            marginTop: theme.space[2],
            padding: `${theme.space[2]} 0 0 0`,
        },
    })),
);

export const StyledDesktopNav = styled(Container)(({ theme }) => ({
    display: 'none',
    padding: `${theme.space[3]} 0 0 0`,
    width: 'unset',
    [breakpoints.sm]: {
        display: 'flex',
        flexDirection: 'row',
        flexGrow: 1,
        flexBasis: 0,
        alignContent: 'center',
        justifyContent: 'flex-end',
        alignItems: 'center',
    },
    [breakpoints.xs]: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        flexBasis: 0,
        alignContent: 'center',
        justifyContent: 'center',
        alignItems: 'flex-end',
    },
}));

export const StyledLogoContainer = styled.div(() => ({
    display: 'flex',
    flexGrow: 1,
    flexBasis: 0,
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    [breakpoints.xs]: {
        padding: `${theme.space[4]} 0 ${theme.space[2]} 0`,
        justifyContent: 'flex-start',
    },
    [breakpoints.sm]: {
        padding: `${theme.space[4]} 0 ${theme.space[2]} 0`,
        justifyContent: 'flex-start',
    },
    [breakpoints.md]: {
        padding: 0,
        justifyContent: 'center',
    },
}));

export const StyledLogo = styled(Logo)(({ theme }) => ({
    display: 'flex',
    height: theme.space[5],
    width: 253,
    [breakpoints.xs]: {
        height: theme.space[4],
        width: 152,
    },
    [mq(0, 'xs')]: {
        height: '11px',
        width: '76px',
    },
}));

export const StyledCheckoutBannerTextLinkDesktop = styled(Link)<{
    textColor: string;
    withCloseBanner?: boolean;
    showTopBanner?: boolean;
    desktop?: boolean;
    withDecoration?: boolean;
    justifyContent?: string;
}>(
    ({ theme, textColor }) => ({
        ...theme.mixins.useTextStyle('bodySmall'),
        color: textColor ? textColor : theme.colors.white,
        textAlign: 'center',
        textJustify: 'auto',
        display: 'flex',
        flexBasis: 0,
        flexGrow: 1,
        letterSpacing: 0,
        alignItems: 'center',
        justifyContent: 'flex-start',
        lineHeight: theme.lineHeights['base'],
        padding: `0 ${theme.space[1]} 0 0`,
        [breakpoints.xs]: {
            display: 'none',
        },
        [breakpoints.sm]: {
            display: 'none',
        },
        [breakpoints.md]: {
            display: 'flex',
        },
    }),
    ifProp('desktop', ({ theme }) => ({
        padding: 'unset',
        marginTop: 'unset',
        lineHeight: `${theme.space[5]}`,
    })),
    ifProp('withDecoration', () => ({
        textDecoration: 'underline',
    })),
    ifProp('justifyContent', ({ justifyContent }) => ({
        justifyContent: justifyContent,
    })),
);
export const StyledIconButtonCheckoutBannerDesktop = styled(ResetButton)<StyledIconButtonProps>(
    ({ theme }) => ({
        display: 'flex',
        padding: `0 0 0 ${theme.space[1]}}`,
        alignItems: 'center',
        justifyContent: 'flex-start',
        alignContent: 'center',
    }),
);
export const StyledDivBanner = styled.div(() => ({
    display: 'flex',
    [breakpoints.xs]: {
        display: 'flex',
    },
    [breakpoints.sm]: {
        display: 'flex',
    },
    [breakpoints.md]: {
        display: 'none',
    },
}));
export const StyledBanner = styled(Banner)(() => ({
    [mq(0, 'sm')]: {
        paddingLeft: 0,
        paddingRight: 0,
    },
}));
export const StyledCheckoutBannerText = styled.p<{
    textColor: string;
    withCloseBanner?: boolean;
    showTopBanner?: boolean;
    desktop?: boolean;
    bannerTextAlignStart?: boolean;
}>(
    ({ theme, textColor }) => ({
        ...theme.mixins.useTextStyle('bodySmall'),
        cursor: 'pointer',
        ':has(:focus-visible)': {
            boxShadow: theme.shadows.accessibility,
        },
        [breakpoints.sm]: {
            display: 'none',
        },
        [breakpoints.md]: {
            color: textColor ? textColor : theme.colors.white,
            flexGrow: 1,
            margin: '0 auto',
            flexBasis: 0,
            justifyContent: 'flex-start',
            textAlign: 'center',
            textJustify: 'auto',
            display: 'flex',
            letterSpacing: 0,
            alignItems: 'center',
            lineHeight: theme.lineHeights['base'],
            padding: `0 0 0 ${theme.space[6]}`,
        },
        [breakpoints.xs]: {
            display: 'none',
        },
    }),
    ifProp('desktop', ({ theme }) => ({
        padding: 'unset',
        marginTop: 'unset',
        lineHeight: `${theme.space[5]}`,
    })),
    ifProp('bannerTextAlignStart', () => ({
        flexGrow: 'unset',
        margin: 'unset',
        flexBasis: 'unset',
        justifyContent: 'flex-start',
    })),
);
